import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { AsideDialog, UnsavedChangesDialog } from '@mews-commander/ui';
import { FormInputField, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DialogProps, Stack } from '@optimus-web/core';
import { DateIntervalFields, InjectedFormProps, withHookForm } from '@mews-commander/form';
import { CREATE_VOUCHER_CODE_ENDING_POINT } from '../../../../../utilities/userFlowTestIds';
import { FIELD_NAMES, FormValues } from './Form';

type VoucherCodeContainerProps = InjectedFormProps &
    Pick<DialogProps, 'title'> & {
        active: boolean;
        isLoading: boolean;
        onConfirm: () => Promise<void>;
        onCancel: () => void;
    };
const VoucherCodeContainer = ({ active, isLoading, title, submit, onCancel }: VoucherCodeContainerProps) => {
    const [unsaved, setUnsaved] = useState(false);
    const { reset, formState } = useFormContext();

    useEffect(() => {
        if (!active) {
            reset();
        }
    }, [active, reset]);

    const handleDiscard = () => {
        setUnsaved(false);
        onCancel?.();
    };

    const handleClose = () => {
        if (formState.isDirty) {
            setUnsaved(true);
            return;
        }

        handleDiscard();
    };

    const actions = [
        {
            children: <LocalizedText textKey={TextKey.Save} />,
            disabled: isLoading || !formState.isValid,
            variant: 'primary',
            onClick: submit,
            'data-test-id': CREATE_VOUCHER_CODE_ENDING_POINT,
            type: 'submit',
        },
    ] satisfies DialogProps['actions'];

    return (
        <>
            <AsideDialog active={active} actions={actions} title={title} onClose={handleClose} loading={isLoading}>
                <Stack vertical spacing="200">
                    <FormInputField
                        required
                        size="medium"
                        name={FIELD_NAMES.VALUE}
                        helper={
                            !formState.errors[FIELD_NAMES.VALUE] && (
                                <LocalizedText textKey={TextKey.MaximumCharacterLengthShortInput} />
                            )
                        }
                        label={<LocalizedText textKey={TextKey.VoucherCode} />}
                    />
                    <DateIntervalFields
                        startDateProps={{
                            name: FIELD_NAMES.VALIDITY_START,
                            label: <LocalizedText textKey={TextKey.VoucherActiveFrom} />,
                        }}
                        endDateProps={{
                            name: FIELD_NAMES.VALIDITY_END,
                            label: <LocalizedText textKey={TextKey.VoucherActiveUntil} />,
                        }}
                        size="medium"
                        mode="localized"
                    />
                </Stack>
            </AsideDialog>
            {unsaved ? (
                <UnsavedChangesDialog onCancel={() => setUnsaved(false)} onDiscardChanges={handleDiscard} />
            ) : null}
        </>
    );
};

export const VoucherCodeDialogForm = withHookForm<FormValues, VoucherCodeContainerProps>()(VoucherCodeContainer);
